import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Space,
  Table,
  Button,
  Select,
  Form,
  Input,
  Popconfirm,
  Spin,
} from "antd";
import { ProfileOutlined } from "@ant-design/icons";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type { InputRef } from "antd";
import type { FilterConfirmProps } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { cloneDeep } from "lodash";
import { RoleType } from "../../models/Role";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { activeUser } from "../../services/AuthServices";
import { RegistrationStatus } from "../../models/Registration";
import { Participant } from "../../models/Participant";
import { PdfPreview } from "../../components/PdfPreview/PdfPreview";
import { getParticipantListReportTemplate } from "../../document-templates/ParticipantListReportTemplate";
import {
  getAllParticipants,
  deleteParticipant,
} from "../../services/ParticipantService";
import { formatDate, formatTime } from "../../core/Utility";
import { getAllEvents } from "../../services/EventService";
import "./ParticipantListPage.scss";
import { Toast } from "primereact/toast";
import CustomButton from "../../components/AccommodationData/CustomButton/CustomButton";
import EmailModal from "../../components/EmailModal/EmailModal";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage } from "../../store-redux/slices/ChangePageSlice";
import { setFiltersParticipant } from "../../store-redux/slices/FiltersParticipantSlice";
import { setPageItems } from "../../store-redux/slices/ChangeItemsPerPage";

const ParticipantListPage = () => {
  const navigate = useNavigate();
  const { setBreadcrumb } = useApplicationContext();

  const toast = useRef<any>(null);
  const [participantList, setParticipantList] = useState<Participant[]>([]);
  const [filteredParticipantList, setFilteredParticipantList] = useState<
    Participant[]
  >([]);
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const [activeSpinner, setActiveSpinner] = useState<boolean>(false);
  const [allEventOptions, setAllEventOptions] = useState<
    { text: string; value: string }[]
  >([]);
  const [typeOptions, setTypeOptions] = useState<
    { text: string; value: string }[]
  >([]);
  const [yearOptions, setYearOptions] = useState<
    { text: string; value: string }[]
  >([]);
  const [companyOptions, setCompanyOptions] = useState<
    { text: string; value: string }[]
  >([]);
  const [filters, setFilters] = useState({});
  const [deleteId, setDeleteId] = useState<string>("");
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [openExportButtons, setOpenExportButtons] = useState<boolean>(false);
  const searchInput = useRef<InputRef>(null);
  const [pageNumber, setPageNumber] = useState<any>(1);

  const [pdfData, setPdfData] = useState<any>(
    getParticipantListReportTemplate(filteredParticipantList)
  );

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [paginationNumber, setPaginationNumber] = useState<any>(30);

  ///// IMPLEMENTATION REDUX
  const dispatch = useDispatch();
  const currentPage = useSelector((state: any) => state.changePage.currentPage);

  const filtersParticipantState = useSelector(
    (state: any) => state.filterParticipant.filters
  );

  const itemsPerPage = useSelector(
    (state: any) => state.changeItemsPerPage.currentItemsPage
  );

  ///// END IMPLEMENTATION REDUX

  const statusOptions = [
    { text: "Uplaćeno", value: RegistrationStatus.Paid },
    { text: "Rezervisano", value: RegistrationStatus.Reserved },
    { text: "Odobreno", value: RegistrationStatus.Approved },
  ];

  useEffect(() => {
    setBreadcrumb(["Učesnici", "Lista učesnika"]);
    fetchAllParticipants();
  }, []);

  const fetchAllParticipants = () => {
    setActiveSpinner(true);
    let copyOfNewParticipants: Participant[] = [];

    getAllParticipants()
      .then((response) => {
        response.data.result.map((item: any) => {
          let oneParticipant = cloneDeep(item);
          oneParticipant.companyName =
            oneParticipant?.registration?.registrationCompanyName ?? "/";
          copyOfNewParticipants.push(oneParticipant);
        });

        let sortedParticipant = [...copyOfNewParticipants];
        sortedParticipant.sort(function (a: any, b: any) {
          var datumA: any = new Date(a?.registration?.createdAt);
          var datumB: any = new Date(b?.registration?.createdAt);

          return datumB - datumA;
        });

        setParticipantList(sortedParticipant);
        setFilteredParticipantList(sortedParticipant);

        setPageNumber(currentPage); /// - SET NUMBER PAGE FROM STORE

        let newTypeOptions: { text: string; value: string }[] = [];
        response.data.result?.map((item: any) => {
          newTypeOptions.push({ text: item.type, value: item.type });
        });
        newTypeOptions = newTypeOptions.filter(
          (
            value,
            index,
            self //remove duplicate
          ) => index === self.findIndex((t) => t.text === value.text)
        );
        setTypeOptions(newTypeOptions);
      })
      .then(() => {
        getAllEvents().then((eventResponse) => {
          let newEventOptions: { text: string; value: string }[] = [];
          let newYearOptions: { text: string; value: string }[] = [];
          eventResponse?.data?.result?.map((eventItem: any) => {
            newEventOptions.push({
              text: eventItem.name,
              value: eventItem.name,
            });
            newYearOptions.push({
              text: new Date(eventItem.dateFrom).getFullYear().toString(),
              value: new Date(eventItem.dateFrom).getFullYear().toString(),
            });
          });
          setAllEventOptions(newEventOptions);
          newYearOptions = newYearOptions.filter(
            (
              value,
              index,
              self //remove duplicate
            ) => index === self.findIndex((t) => t.text === value.text)
          );
          setYearOptions(newYearOptions);
        });
      })
      .finally(() => setActiveSpinner(false))
      .catch((err) => console.log(err));
  };

  type DataIndex = keyof Participant;
  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<Participant> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, border: "none" }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              close();
              setSearchedColumn("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Poništi
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#C7363D" : undefined }} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
            color: "#0D4077",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<Participant | any> = [
    {
      title: "Red. broj",
      width: 100,
      dataIndex: "firstName",
      key: "firstName",
      render: (firstName, text, index) => {
        return (
          <span className="blue-text">
            {(pageNumber - 1) * itemsPerPage + index + 1}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "registration",
      key: "registration",
      filters: statusOptions,
      filteredValue: filtersParticipantState?.filters?.registration,
      defaultSortOrder:
        filtersParticipantState?.sort?.columnKey == "registration" &&
        filtersParticipantState?.sort?.order,
      onFilter: (value: any, record: any) =>
        record?.registration?.status === value,
      render: (text, record) => (
        <span className="blue-text name-container">
          <span
            className={
              record?.registration?.status === RegistrationStatus.Pending
                ? "status-dot status-dot-pending"
                : record?.registration?.status === RegistrationStatus.Rejected
                ? "status-dot status-dot-rejected"
                : record?.registration?.status === RegistrationStatus.Approved
                ? "status-dot status-dot-approved"
                : record?.registration?.status === RegistrationStatus.Reserved
                ? "status-dot status-dot-reserved"
                : record?.registration?.status === RegistrationStatus.Paid
                ? "status-dot status-dot-paid"
                : ""
            }
          >
            _
          </span>
          {record?.registration?.status}
        </span>
      ),
    },
    {
      title: "Ime",
      dataIndex: "firstName",
      key: "firstName",
      defaultSortOrder:
        filtersParticipantState?.sort?.columnKey == "firstName" &&
        filtersParticipantState?.sort?.order,
      filteredValue: filtersParticipantState?.filters?.firstName,
      sorter: (a, b) => (a.firstName < b.firstName ? -1 : 1),
      //defaultSortOrder: "ascend",
      render: (firstName, record) => {
        return <span className="blue-text name-container">{firstName}</span>;
      },
      ...getColumnSearchProps("firstName"),
    },
    {
      title: "Prezime",
      dataIndex: "lastName",
      key: "lastName",
      defaultSortOrder:
        filtersParticipantState?.sort?.columnKey == "lastName" &&
        filtersParticipantState?.sort?.order,
      filteredValue: filtersParticipantState?.filters?.lastName,
      sorter: (a, b) => (a.lastName < b.lastName ? -1 : 1),
      render: (lastName) => <span className="blue-text">{lastName}</span>,
      ...getColumnSearchProps("lastName"),
    },
    {
      title: "Događaj",
      dataIndex: "event",
      key: "event",
      filteredValue: filtersParticipantState?.filters?.event,
      defaultSortOrder:
        filtersParticipantState?.sort?.columnKey == "event" &&
        filtersParticipantState?.sort?.order,
      sorter: (a, b) =>
        (a?.event?.name ?? "") < (b?.event?.name ?? "") ? -1 : 1,
      filters: allEventOptions, //@ts-ignore
      onFilter: (value: string, record) => record?.event?.name === value,
      render: (event) => <span className="blue-text">{event?.name}</span>,
    },
    {
      title: "Tip učesnika",
      dataIndex: "type",
      key: "type",
      filteredValue: filtersParticipantState?.filters?.type,
      defaultSortOrder:
        filtersParticipantState?.sort?.columnKey == "type" &&
        filtersParticipantState?.sort?.order,
      sorter: (a, b) => ((a?.type ?? "") < (b?.type ?? "") ? -1 : 1),
      filters: typeOptions, //@ts-ignore
      onFilter: (value: string, record) => record?.type === value,
      render: (type) => (
        <span className="blue-text">
          {type == "Nepoznat" || type == null || !type ? "/" : type}
        </span>
      ),
    },
    {
      title: "Pravno lice",
      dataIndex: "companyName",
      key: "companyName",
      defaultSortOrder:
        filtersParticipantState?.sort?.columnKey == "companyName" &&
        filtersParticipantState?.sort?.order,
      filteredValue: filtersParticipantState?.filters?.companyName,
      sorter: (a, b) =>
        (a?.companyName ?? "") < (b?.companyName ?? "") ? -1 : 1,
      render: (companyName) => (
        <span className="blue-text">{companyName ?? "/"}</span>
      ),
      ...getColumnSearchProps("companyName"),
    },
    {
      title: "Telefon",
      dataIndex: "phone",
      key: "phone",
      filteredValue: filtersParticipantState?.filters?.phone,
      render: (phone) => <span className="blue-text">{phone}</span>,
      ...getColumnSearchProps("phone"),
    },
    {
      title: "Opcije",
      fixed: "right",
      width: 240,
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <span
            className="action-button"
            onClick={() => handleViewButton(record.id as string)}
          >
            Izmena/Pregled
          </span>
          {(activeUser?.role.name === RoleType.Admin ||
            activeUser?.role.name === RoleType.Employee) && (
            <Popconfirm
              title="Brisanje učesnika?"
              description="Da li ste sigurni da želite da izbrišete učesnika?"
              onConfirm={() => {
                setActiveSpinner(true);
                deleteParticipant(deleteId)
                  .then(() => {
                    toast?.current?.show({
                      severity: "success",
                      detail: "Uspešno izbrisan učesnik",
                      life: 3000,
                    });
                    fetchAllParticipants();
                  })
                  .finally(() => setActiveSpinner(false))
                  .catch((err: any) => console.log(err));
              }}
              onCancel={() => {}}
              okText="Da"
              cancelText="Ne"
            >
              <span
                className="action-button"
                onClick={() => setDeleteId(record.id as string)}
              >
                Brisanje
              </span>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const handleViewButton = (participantId: any) => {
    return navigate(`/participant-overview/${participantId}`);
  };

  const handleOpenFilters = () => {
    setOpenFilter(!openFilter);
  };

  const handeSelection = (name: string, newValue: any) => {
    setFilters({ ...filters, [name]: newValue });
  };

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getParticipantListReportTemplate(filteredParticipantList);
    setPdfData(newPdf);
  }, [filteredParticipantList]);

  const handleOpenExportButtons = () => {
    setOpenExportButtons(!openExportButtons);
  };

  const handleEmailButton = () => {
    setShowEmailModal(!showEmailModal);
  };

  const handleExcelButton = () => {
    const filterObject = filteredParticipantList.map((el: any) => {
      return {
        Ime: el.firstName,
        Prezime: el.lastName,
        Dogadjaj: el.event.name,
        //Tip: el.type,
        Kompanija: el.companyName ?? "/",
        Telefon: el.phone,
        Email: el.email
      };
    });

    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(filterObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAsExcelFile(excelBuffer, "Lista učesnika ");
    });
    setOpenExportButtons(false);
  };

  const saveAsExcelFile = (buffer: any, fileName: string) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(
          data,
          fileName +
            "_" +
            formatDate(new Date()) +
            "_" +
            formatTime(new Date()) +
            EXCEL_EXTENSION
        );
      }
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="participant-list">
        <Spin spinning={activeSpinner}>
          <div className="overview-legend-right">
            <div className="legend-row" style={{ marginRight: "15px" }}>
              <CustomButton
                danger={false}
                htmlType={"submit"}
                key={2}
                type={"primary"}
                function={() => {
                  // setResetData(!resetData);
                  // dispatch(resetFiltersParticipant());
                  window.location.reload();
                }}
                text={"Resetuj sve filtere"}
                style={{ marginTop: "2rem" }}
              />
            </div>
            <p className="legend-text">Statusi:</p>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-paid" />
              <p className="legend-icon-paid-text">Uplaćeno</p>
            </div>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-reserved" />
              <p className="legend-icon-reserved-text">Rezervisano</p>
            </div>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-approved" />
              <p className="legend-icon-approved-text">Odobreno</p>
            </div>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-rejected" />
              <p className="legend-icon-rejected-text">Odbijeno</p>
            </div>
            <div className="legend-row">
              <ProfileOutlined className="legend-icon-pending" />
              <p className="legend-icon-pending-text">Na čekanju</p>
            </div>
            {/* <div className="btn-container">
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  handleOpenFilters();
                }}
                text={" Dodatni filteri"}
              />
            </div> */}
          </div>
          {openFilter ? (
            <div className="filters-container">
              <Form.Item name="selectedFilters" label="Godina:">
                <Select
                  placeholder="Izaberite godinu"
                  options={yearOptions}
                  onChange={(newValue) => handeSelection("year", newValue)}
                />
              </Form.Item>
              <Form.Item name="selectedFilters" label="Tip učesnika:">
                <Select
                  placeholder="Izaberite tip učesnika "
                  options={typeOptions}
                  onChange={(newValue) =>
                    handeSelection("participantType", newValue)
                  }
                />
              </Form.Item>
              <Form.Item name="selectedFilters" label="Pravno lice:">
                <Select
                  placeholder="Izaberite pravno lice"
                  options={companyOptions}
                  onChange={(newValue) => handeSelection("company", newValue)}
                />
              </Form.Item>
            </div>
          ) : null}
          <Table
            sticky={true}
            scroll={{ x: 1500 }}
            columns={columns}
            rowKey={(data) => {
              return data.id;
            }}
            pagination={{
              current: pageNumber,
              onChange(current) {
                setPageNumber(current);
                dispatch(setCurrentPage(current)); /// DISPATCH ACTION FROM REDUX
              },
              pageSize: itemsPerPage,
              defaultPageSize: 30,
              showSizeChanger: true,
              pageSizeOptions: ["30", "50", "100"],
              onShowSizeChange(current, pageSize) {
                dispatch(setPageItems(pageSize));
              },
            }}
            dataSource={participantList}
            onChange={(pagination, filters, sorter, extra) => {
              setPaginationNumber(pagination.pageSize);
              setFilteredParticipantList(extra.currentDataSource);

              dispatch(
                setFiltersParticipant({
                  filters: filters,
                  sort: sorter,
                })
              );
            }}
            className="table-container"
          />
          <div className="btn-container footer-btn-container">
            <CustomButton
              // danger={false}
              // htmlType={"submit"}
              type={"primary"}
              function={() => {
                setShowEmailModal(true);
              }}
              text={"E-mail obaveštenje"}
            />
            <CustomButton
              danger={false}
              htmlType={"submit"}
              type={"primary"}
              function={() => {
                handleOpenExportButtons();
              }}
              text={"Export"}
            />
          </div>
          {openExportButtons ? (
            <div className="btn-container footer-btn-container">
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  handleExcelButton();
                }}
                text={"Excel"}
              />
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                }}
                text={"PDF"}
              />
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                }}
                text={"Štampa"}
              />
            </div>
          ) : null}
          <div className="document-preview">
            <PdfPreview
              pdfDefinition={pdfData}
              downloadDocument={downloadPdfDocument}
              printDocument={printPdfDocument}
              documentName={`Lista_ucesnika_${
                formatDate(new Date()) + "_" + formatTime(new Date())
              }.pdf`}
            />
          </div>
        </Spin>
        {showEmailModal && (
          <EmailModal
            toast={toast}
            participantList={participantList}
            handleEmailButton={handleEmailButton}
            showEmailModal={showEmailModal}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default ParticipantListPage;
